import React from "react";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/pro-light-svg-icons";
import Input from "src/components/Input";
import SelectSearch, { OptionItem } from "src/components/SelectSearch";
import { FormLabel } from "src/components/atoms/FormLabel";
import { DynamicFieldProps } from "../types";
import {
  PurchaseDetailsContainer,
  LineItemsTable,
  LineItemRow,
  LineItemColumn,
  ColumnLabel,
  RemoveButtonContainer,
  AddButtonContainer,
} from "../styles";

const TYPE_OPTIONS: OptionItem[] = [
  { id: "Supplies", value: "Supplies" },
  { id: "Equipment", value: "Equipment" },
  { id: "Service", value: "Service" },
];

export const DynamicPurchaseDetailsField = ({ dynamicField }: DynamicFieldProps) => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: dynamicField.id,
  });

  const handleAddLineItem = () => {
    append({
      type: "",
      description: "",
      quantity: 0,
      unitcost: 0,
    });
  };

  const handleRemoveLineItem = (index: number) => {
    remove(index);
  };

  return (
    <PurchaseDetailsContainer>
      <FormLabel required={dynamicField.rules?.required}>{dynamicField.label}:</FormLabel>

      <LineItemsTable>
        <thead>
          <tr>
            <ColumnLabel>Type</ColumnLabel>
            <ColumnLabel>Description</ColumnLabel>
            <ColumnLabel>Quantity</ColumnLabel>
            <ColumnLabel>Unit Cost</ColumnLabel>
            <ColumnLabel style={{ width: "50px" }}>Action</ColumnLabel>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, index) => (
            <LineItemRow key={field.id}>
              <LineItemColumn>
                <Controller
                  control={control}
                  name={`${dynamicField.id}.${index}.type`}
                  rules={{ required: "Type is required" }}
                  render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <div style={{ minWidth: "100px" }}>
                      <SelectSearch
                        id={`${dynamicField.id}-type-${index}`}
                        options={TYPE_OPTIONS}
                        defaultValue={value}
                        placeholder="Select type"
                        name={name}
                        onChange={(option) => onChange(option.id)}
                        error={error}
                      />
                    </div>
                  )}
                />
              </LineItemColumn>

              <LineItemColumn>
                <Controller
                  control={control}
                  name={`${dynamicField.id}.${index}.description`}
                  rules={{ required: "Description is required" }}
                  render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Input
                      name={name}
                      value={value || ""}
                      placeholder="Enter description"
                      onChange={onChange}
                      error={error}
                      style={{ width: "100%" }}
                    />
                  )}
                />
              </LineItemColumn>

              <LineItemColumn>
                <Controller
                  control={control}
                  name={`${dynamicField.id}.${index}.quantity`}
                  rules={{
                    required: "Quantity is required",
                    pattern: {
                      value: /^\d+(\.\d+)?$/,
                      message: "Invalid quantity",
                    },
                  }}
                  render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Input
                      type="number"
                      name={name}
                      value={value || ""}
                      placeholder="0"
                      onChange={onChange}
                      error={error}
                      style={{ width: "100px" }}
                    />
                  )}
                />
              </LineItemColumn>

              <LineItemColumn>
                <Controller
                  control={control}
                  name={`${dynamicField.id}.${index}.unitcost`}
                  rules={{
                    required: "Unit cost is required",
                    pattern: {
                      value: /^\d+(\.\d+)?$/,
                      message: "Invalid cost",
                    },
                  }}
                  render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Input
                      type="number"
                      name={name}
                      value={value || ""}
                      placeholder="0.00"
                      onChange={onChange}
                      error={error}
                      style={{ width: "120px" }}
                      step="0.01"
                    />
                  )}
                />
              </LineItemColumn>

              <RemoveButtonContainer>
                <button
                  type="button"
                  onClick={() => handleRemoveLineItem(index)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#d32f2f",
                    fontSize: "16px",
                    padding: "4px 8px",
                  }}
                  title="Remove line item"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </RemoveButtonContainer>
            </LineItemRow>
          ))}
        </tbody>
      </LineItemsTable>

      <AddButtonContainer>
        <button
          type="button"
          onClick={handleAddLineItem}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#1976d2",
            fontSize: "18px",
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: "500",
          }}
          title="Add new line item"
        >
          <FontAwesomeIcon icon={faPlus} />
          Add Line Item
        </button>
      </AddButtonContainer>
    </PurchaseDetailsContainer>
  );
};
